// The one picker for the personal data categories a RoPA entry, a breach or an AI system records:
// the general categories, then the nine kinds of special category data (GDPR Art. 9(1)) as their own
// multi-select. A record made before the split keeps its "special category" value, shown with a
// dashed outline until the member names the kinds; any other value already on the record (an older
// free-text category) is shown and can be removed, never silently dropped.

import { theme, type as text } from "../theme";
import {
  GENERAL_DATA_CATEGORIES, SPECIAL_CATEGORY_DATA, UNSPECIFIED_SPECIAL_CATEGORY, UNSPECIFIED_SPECIAL_CATEGORY_LABEL,
  type DataCategory,
} from "../lib/dataCategories";

const KNOWN = new Set([...GENERAL_DATA_CATEGORIES, ...SPECIAL_CATEGORY_DATA].map((c) => c.id));

function Chip({ label, on, dashed, title, onClick }: { label: string; on: boolean; dashed?: boolean; title?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={on}
      style={{
        padding: "4px 12px", borderRadius: 3, fontSize: 12, cursor: "pointer",
        border: dashed ? `1px dashed ${theme.color.warningText}` : on ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)",
        background: dashed ? "var(--c-warningBg)" : on ? "var(--c-accentBg)" : "var(--c-panel)",
        color: dashed ? "var(--c-warningText)" : on ? "var(--c-accentText)" : theme.color.muted,
        fontWeight: on ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}

export function DataCategoryPicker({ selected, onChange }: { selected: string[]; onChange: (next: string[]) => void }) {
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const row = (options: readonly DataCategory[]) => options.map((c) => (
    <Chip key={c.id} label={c.label} on={selected.includes(c.id)} onClick={() => toggle(c.id)} />
  ));
  const unspecified = selected.includes(UNSPECIFIED_SPECIAL_CATEGORY);
  const others = selected.filter((id) => id !== UNSPECIFIED_SPECIAL_CATEGORY && !KNOWN.has(id));
  const wrap = { display: "flex", flexWrap: "wrap" as const, gap: 6 };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={wrap}>{row(GENERAL_DATA_CATEGORIES)}</div>
      <div>
        <div style={{ ...text.label, marginBottom: 2 }}>Special category data (GDPR Art. 9)</div>
        <div style={{ ...text.hint, marginBottom: 6 }}>
          Choose every kind involved. Each needs its own Art. 9(2) condition. Criminal offence data is
          recorded above, not here.
        </div>
        <div style={wrap}>
          {row(SPECIAL_CATEGORY_DATA)}
          {unspecified && (
            <Chip
              label={`${UNSPECIFIED_SPECIAL_CATEGORY_LABEL} ×`} on dashed
              title="Recorded before special category data was split into its kinds. Choose the kinds that apply, then remove this."
              onClick={() => toggle(UNSPECIFIED_SPECIAL_CATEGORY)}
            />
          )}
        </div>
        {unspecified && (
          <div style={{ ...text.hint, color: "var(--c-warningText)", marginTop: 6 }}>
            This record says special category data without saying which kind. Choose the kinds above,
            then remove the dashed one.
          </div>
        )}
      </div>
      {others.length > 0 && (
        <div>
          <div style={{ ...text.hint, marginBottom: 6 }}>Also recorded (not one of the categories above):</div>
          <div style={wrap}>
            {others.map((id) => <Chip key={id} label={`${id} ×`} on onClick={() => toggle(id)} title="Remove" />)}
          </div>
        </div>
      )}
    </div>
  );
}
